import { TResponsiveTableColumn, TResponsiveTableRow } from '@apia/table';
import { TableController } from './table/TableController';
import { arrayOrArray, TApiaFunction, TApiaFunctionPageInfo } from '@apia/util';
import { TFilter } from './filters/types';
import ApiaApi, { makeApiaUrl } from '../apiaApi';
import { Filter } from './filters/Filter';
import { StructureResponse } from './types';
import { throttle } from 'lodash-es';

export type QueryControllerStructure = {
  columns: TResponsiveTableColumn[];
  description: string;
  filters: TFilter[];
  hasAdditionalInformation: boolean;
  isDynamic: boolean;
  openFiltersAutomatically: boolean;
  runFiltersAutomatically: boolean;
  title: string;
};

export abstract class QueryController {
  private hasInited = false;
  private initializing = false;
  public structure: QueryControllerStructure | null = null;
  public tableController: TableController = new TableController();

  constructor(public ajaxUrl: string) {
    this.initEvents();
  }

  private initEvents() {
    this.tableController.on('onChangeSelection', (ev) => {
      ev;
    });
    this.tableController.on('onFilterBlur', (ev) => {
      ev;
    });
    this.tableController.on('onFilterChange', (ev) => {
      const filter = this.tableController.state.filters.get(ev.id);
      if (filter?.filterState.runAutomatically) {
        this.refresh();
      }
    });
    this.tableController.on('onFilterPressEnter', this.refresh.bind(this));
    this.tableController.on('onRowClick', (ev) => {
      ev;
    });
    this.tableController.on('onSelectRows', (ev) => {
      ev;
    });
    this.tableController.on('onSortChange', (ev) => {
      this.sort(ev.column.name);
    });
    this.tableController.on('onPage', this.page.bind(this));
    this.tableController.on('onRefresh', this.refresh.bind(this));
  }

  protected getLoadStructureParameters(): Record<string, unknown> {
    return {
      ajaxUrl: this.ajaxUrl,
      action: 'init',
      react: true,
      //Importante para forzar paginacion en querys de modal ya que no existe parametro aún
      forcePagination: true,
    };
  }
  protected getPageParameters(): Record<string, unknown> {
    return {
      ajaxUrl: this.ajaxUrl,
      action: 'page',
    };
  }
  protected getRefreshParameters(): Record<string, unknown> {
    return {
      ajaxUrl: this.ajaxUrl,
      action: 'refresh',
      react: true,
    };
  }

  protected getSortParameters(): Record<string, unknown> {
    return {
      ajaxUrl: this.ajaxUrl,
      action: 'sort',
      react: true,
      orderJustThisColumn: true,
    };
  }

  protected parseLoadStructureResponse(
    response: Record<string, unknown>,
  ): QueryControllerStructure {
    const typedResponse = response as StructureResponse;

    const retrievedColumns = arrayOrArray(typedResponse.columns.column);

    return {
      columns: retrievedColumns.map((c) => {
        const column: TResponsiveTableColumn = {
          label: c.title,
          name: c.name,
          additionalData: c,
          allowSorting: c.enableOrder === '1',
          ariaLabel: c.title,
          currentSorting: c.order as 'D',
          hidden: c.hide === '1',
          hideFromAccordion: c.hide === '1',
          id: c.name,
          showAsAdditional: c.asMoreInformation,
          title: c.title,
          width: c.width,
        };

        return column;
      }),
      description: typedResponse.queryData.description,
      filters: arrayOrArray(typedResponse.filters.filter)
        .filter((f) => !!f.column)
        .map((f, i) => {
          const filter: TFilter = {
            id: `filter${String(i + 1)}`, // Id generado en APIA
            value: f.currentValue,
            avoidLabel: false,
            column: f.column,
            isAdditional: f.asAdditional,
            isHidden: f.hideFilterOption,
            title: f.title,
            tooltip: f.toolTip,
            type: f.type as 'S',
            onChange: () => {},
            options: [],
            placeholder: f.title,
            readonly: f.readonly,
            required: f.required === 'true',
            runAutomatically:
              typedResponse?.filters.runFiltersAutomatically ||
              f.detectOnChange,
          };

          return filter;
        }),
      hasAdditionalInformation: !!retrievedColumns.find(
        (c) => c.asMoreInformation,
      ),
      isDynamic: false,
      title: typedResponse.queryData.title,
      openFiltersAutomatically: typedResponse.filters.openFiltersAutomatically,
      runFiltersAutomatically: typedResponse.filters.runFiltersAutomatically,
    };
  }

  protected parseRefreshResponse(response?: TApiaFunction | null) {
    const columns = [...this.tableController.state.columns.values()];
    const rows = response?.function?.messages?.result?.table?.row;
    const pageState = response?.function.messages.result.pageInfo;

    this.updatePageState(pageState);
    this.updateRows([
      ...arrayOrArray(rows).map((r) => {
        return {
          forbidSelection: r.unselectableTR,
          initiallySelected: r.selected,
          isLocked: r.isLocked,
          rowProps: r,
          cells: [
            columns.find((c) => c.showAsAdditional) ? {} : undefined,
            ...columns.filter((c) => c.hidden).map(() => ({})),
            ...arrayOrArray(r.cell).map((c) => ({ children: c })),
          ].filter(Boolean),
          className: r.classToAdd,
          id: r.id,
        } as TResponsiveTableRow;
      }),
    ]);
  }

  protected async getStructure(): Promise<QueryControllerStructure> {
    const result = await ApiaApi.post(
      makeApiaUrl(this.getLoadStructureParameters()),
    );

    if (!result?.data) {
      throw new Error('Cannot load Input query structure');
    }

    return this.parseLoadStructureResponse(result.data);
  }

  protected parseStructure(structure: QueryControllerStructure) {
    const columns = new Map(
      arrayOrArray(structure.columns).map((c) => [c.name, c]),
    );
    const filters = new Map(
      arrayOrArray(structure.filters).map((f) => [f.id, new Filter(f)]),
    );

    this.tableController.updateState({ columns, filters });
  }

  protected updatePageState(pageInfo?: TApiaFunctionPageInfo) {
    const currentPage = Number(pageInfo?.currentPage ?? 0);
    const totalPages = Number(pageInfo?.pageCount ?? 0);
    const totalRecords = Number(pageInfo?.totalRecords ?? 0);

    this.tableController.updateState({
      pagination: { currentPage, totalPages, totalRecords },
    });
  }

  protected updateRows(rows: TResponsiveTableRow[]) {
    this.tableController.state.rows = rows;
  }

  public async initialLoad() {
    if (this.initializing && !this.hasInited) {
      throw new Error('Cannot initialize the query twice');
    }
    this.initializing = true;

    if (!this.hasInited) {
      this.structure = await this.getStructure();
      this.parseStructure(this.structure);

      this.hasInited = true;
      this.initializing = false;
    }
  }

  public async page(num: number) {
    const page = await ApiaApi.post<TApiaFunction>(
      makeApiaUrl({ ...this.getPageParameters(), pageNumber: num }),
    );

    this.parseRefreshResponse(page?.data);
  }

  protected executeRefresh = throttle(
    async () => {
      const refresh = await ApiaApi.post<TApiaFunction>(
        makeApiaUrl(this.getRefreshParameters()),
      );

      this.parseRefreshResponse(refresh?.data);
    },
    300,
    { leading: false, trailing: true },
  );

  public async refresh() {
    return this.executeRefresh();
  }

  public async sort(column: string) {
    await ApiaApi.post(
      makeApiaUrl({ ...this.getSortParameters(), orderBy: column }),
    );
    this.refresh();
  }
}
